/**
 * Suggestion engine only — include/exclude entity assets in automatic funding suggestions.
 * Spec §9.1.2, Appendix A.6. Shown in Strategy Analysis Setup (not Client Profile Step 5).
 */
import React from 'react';
import { ENTITY_STRUCTURE_FUNDING_DISCLAIMER } from '../../utils/entityFundingUtils.ts';

export type StructureFundingInSuggestions = 'include' | 'exclude';

export interface StructureFundingInSuggestionsFieldsProps {
    value: StructureFundingInSuggestions | '' | undefined;
    onChange: (value: StructureFundingInSuggestions) => void;
    compact?: boolean;
}

export function StructureFundingInSuggestionsFields({ value, onChange, compact = false }: StructureFundingInSuggestionsFieldsProps) {
    const selected = value || 'exclude';

    return (
        <div
            className={
                compact
                    ? 'space-y-3'
                    : 'bg-gradient-to-br from-amber-50 via-white to-orange-50/40 border border-amber-200 rounded-xl p-5 shadow-sm space-y-3'
            }
        >
            {!compact && (
                <div>
                    <h3 className="text-lg font-semibold text-amber-950 mb-1">
                        Trusts and companies in strategy suggestions
                    </h3>
                    <p className="text-sm text-slate-600">
                        We always use these arrangements for Age Pension and care fee estimates. This only affects{' '}
                        <strong>automatic strategy suggestions</strong> — not manual control.
                    </p>
                </div>
            )}
            <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-gray-800 mb-2">
                    Do you want us to include assets held in trusts or companies when suggesting ways to pay for care?
                </legend>
                <label
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selected === 'exclude'
                            ? 'border-amber-500 bg-white ring-2 ring-amber-200'
                            : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                >
                    <input
                        type="radio"
                        name="structureFundingInSuggestions"
                        value="exclude"
                        checked={selected === 'exclude'}
                        onChange={() => onChange('exclude')}
                        className="h-4 w-4 text-amber-600 border-gray-300 focus:ring-amber-500 mt-1"
                    />
                    <span className="text-sm text-gray-800">No — suggest funding from personal assets only</span>
                </label>
                <label
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selected === 'include'
                            ? 'border-amber-500 bg-white ring-2 ring-amber-200'
                            : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                >
                    <input
                        type="radio"
                        name="structureFundingInSuggestions"
                        value="include"
                        checked={selected === 'include'}
                        onChange={() => onChange('include')}
                        className="h-4 w-4 text-amber-600 border-gray-300 focus:ring-amber-500 mt-1"
                    />
                    <span className="text-sm text-gray-800">Yes — you may suggest using trust or company assets</span>
                </label>
            </fieldset>
            <p className="text-xs text-slate-700">{ENTITY_STRUCTURE_FUNDING_DISCLAIMER}</p>
        </div>
    );
}
